import React, { useState } from 'react';
import {
  TextField,
  Button,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';

type NoteEntry = {
  date: Dayjs | null;
  note: string;
};

export default function DiagnosisDetail() {
  const [notes, setNotes] = useState<NoteEntry[]>([{ date: null, note: '' }]);
  const [submittedNotes, setSubmittedNotes] = useState<NoteEntry[]>([]);

  const isFutureDate = (date: Dayjs | null) => {
    return date ? date.isAfter(dayjs(), 'day') : false;
  };

  const handleNoteChange = (index: number, field: keyof NoteEntry, value: string | Dayjs | null) => {
    const updated = [...notes];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setNotes(updated);
  };

  const handleAddRow = () => {
    if (notes.length < 3) {
      setNotes([...notes, { date: null, note: '' }]);
    }
  };

  const handleDeleteRow = (index: number) => {
    const updated = notes.filter((_, i) => i !== index);
    setNotes(updated.length ? updated : [{ date: null, note: '' }]);
  };

  const handleSubmit = async () => {
    const validNotes = notes.filter(n => isFutureDate(n.date) && n.note.trim());
    if (validNotes.length === 0) {
      toast.warning("Please add at least one valid follow-up note with a future date.");
      return;
    }

    setSubmittedNotes(validNotes);
    const payload = validNotes.map(n => ({
      date: n.date?.format('YYYY-MM-DD'),
      note: n.note,
      patientName: "John Doe", // Replace with actual patient name if available
      patientEmail: "milind.padave@periscope-tech.com", // Replace with actual patient email if available


    }));

    try {
      const response = await fetch("https://periscope-tech.app.n8n.cloud/webhook/b44fd346-56e3-43ee-a1bb-3ef1cf8c0610", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({ followUps: payload }) // Adjust this if API expects a different format
      });

      if (!response.ok) throw new Error("Failed to send");

      toast.success("Diagnosis follow-up scheduled successfully");
      console.log("API response:", await response.json());
    } catch (err) {
      console.error("Error:", err);
      toast.error("Failed to send follow-up notes.");
    }
  };

  return (
    <div style={{
      background: '#fff',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      fontFamily: 'Quicksand, sans-serif',
      marginTop: '20px'
    }}>
      <div style={{ textAlign: 'right', color: '#718096', fontSize: '14px', fontWeight: 500 }}>
        July 7, 2025
      </div>

      {/* Current Diagnosis */}
      <div style={{ marginTop: '16px' }}>
        <p style={{ fontWeight: '600', marginBottom: '8px' }}>Current Diagnosis</p>
        <div style={{
          background: '#F7FAFC',
          padding: '16px',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <strong style={{ fontSize: '16px' }}>🩺 Chronic Bronchitis</strong>
          <span style={{ fontSize: '14px', color: '#4A5568' }}>
            A type of COPD characterized by inflammation of the bronchial tubes, resulting in persistent cough and mucus production.
          </span>
        </div>
      </div>

      {/* Severity Assessment */}
      <div style={{ marginTop: '24px' }}>
        <p style={{ fontWeight: '600', marginBottom: '8px' }}>Severity Assessment</p>
        <div style={{
          background: '#E2E8F0',
          height: '6px',
          borderRadius: '3px',
          marginBottom: '8px'
        }}>
          <div style={{
            background: '#F6AD55',
            width: '50%',
            height: '100%',
            borderRadius: '3px'
          }} />
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: '#718096'
        }}>
          <span>Mild</span>
          <span style={{ color: '#F6AD55' }}>Moderate</span>
          <span>Critical</span>
        </div>
      </div>

      {/* Symptoms */}
      <div style={{ marginTop: '24px' }}>
        <p style={{ fontWeight: '600', marginBottom: '8px' }}>Symptoms</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input type="checkbox" checked readOnly style={{ marginRight: '8px' }} />
              <strong>Persistent Cough</strong>
            </label>
            <div style={{ marginLeft: '24px', fontSize: '14px', color: '#4A5568' }}>
              <div>Duration: 3+ weeks</div>
              <div>Productive cough with yellowish-green mucus, worse in the morning</div>
            </div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input type="checkbox" checked readOnly style={{ marginRight: '8px' }} />
              <strong>Shortness of Breath</strong>
            </label>
            <div style={{ marginLeft: '24px', fontSize: '14px', color: '#4A5568' }}>
              <div>Duration: 2 weeks</div>
              <div>Exacerbated by physical activity and environmental triggers.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Follow-up Notes */}
      <div style={{ marginTop: '32px' }}>
        <p style={{ fontWeight: '600', marginBottom: '12px' }}>Follow-up Notes</p>

        <div style={{
          background: '#F7FAFC',
          padding: '16px',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {notes.map((entry, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <DatePicker
                  label="Follow-up Date"
                  value={entry.date}
                  onChange={(date) => handleNoteChange(idx, 'date', date)}
                  disablePast
                  slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
                />
                <TextField
                  placeholder="Follow-up Note"
                  value={entry.note}
                  onChange={(e) => handleNoteChange(idx, 'note', e.target.value)}
                  size="small"
                  sx={{ width: 300 }}
                />
                {notes.length > 1 && (
                  <IconButton color="error" onClick={() => handleDeleteRow(idx)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </div>
            ))}
          </LocalizationProvider>

          {notes.length < 3 && (
            <Button
              onClick={handleAddRow}
              variant="outlined"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            >
              + Add Note
            </Button>
          )}

          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            sx={{ alignSelf: 'flex-start' }}
          >
            Submit Follow-up Notes
          </Button>
        </div>

        {submittedNotes.length > 0 && (
          <ul style={{ marginTop: '16px', paddingLeft: '20px' }}>
            {submittedNotes.map((note, idx) => (
              <li key={idx}>
                <strong>{note.date?.format('YYYY-MM-DD')}</strong>: {note.note}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
